import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { secureStorage } from './secure-storage.service';
import { CookieService } from './cookie.service';

@Injectable({
  providedIn: 'root',
})
export class TokenService {
  private refreshTimer: any = null;
  private tokenGenerationInProgress = false;
  private readonly ACCESS_EXPIRES_AT_KEY = 'eol_access_token_expires_at';

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private localStorage: secureStorage,
    private cookieService: CookieService,
  ) {}

  // Get access token from localStorage
  getAccessToken(): string | null {
    return this.localStorage.getItem('eol_access_token');
  }

  // Get refresh token from localStorage
  getRefreshToken(): string | null {
    return this.localStorage.getItem('eol_ref_token');
  }

  // Get user code from localStorage
  getUserCode(): string | null {
    return this.localStorage.getItem('eol_user_code');
  }

  // Set access token in localStorage
  setAccessToken(token: string): void {
    this.localStorage.setItem('eol_access_token', token);
  }

  // Set refresh token in localStorage
  setRefreshToken(token: string): void {
    this.localStorage.setItem('eol_ref_token', token);
  }

  // Set user code in localStorage
  setUserCode(userCode: string): void {
    this.localStorage.setItem('eol_user_code', userCode);
  }

  // Check if user is authenticated (has both token and user code)
  isAuthenticated(): boolean {
    return !!(this.getAccessToken() && this.getUserCode());
  }

  // Check if user has valid and complete authentication
  hasValidAuthentication(): boolean {
    const accessToken = this.getAccessToken();
    const userCode = this.getUserCode();
    return !!(accessToken && userCode && !this.isTokenExpired());
  }

  // Check if user has access token (for free search scenarios)
  hasAccessToken(): boolean {
    return !!this.getAccessToken();
  }

  // Check if token needs to be generated (for non-authenticated users)
  needsTokenGeneration(): boolean {
    const accessToken = this.getAccessToken();
    const userCode = this.getUserCode();

    // If user is authenticated and has valid token, no action needed
    if (accessToken && userCode && !this.isTokenExpired()) {
      return false;
    }

    // If user is authenticated but token is expired or missing
    if (userCode && (!accessToken || this.isTokenExpired())) {
      return true;
    }

    // If no authentication at all, generate free search token
    if (!accessToken && !userCode) {
      return true;
    }

    // If has token but no user code (free search scenario) and token is expired
    if (accessToken && !userCode && this.isTokenExpired()) {
      return true;
    }

    return false;
  }

  // Get token expiration time from JWT
  getTokenExpirationTime(): number | null {
    const token = this.getAccessToken();
    if (!token) return null;

    try {
      const parts = token.split('.');
      if (parts.length < 2) {
        // Not a JWT; fall back to stored expires_at if available
        const fallback = this.localStorage.getItem(this.ACCESS_EXPIRES_AT_KEY);
        return fallback ? Number(fallback) : null;
      }

      // JWT uses base64url encoding, need to convert to standard base64
      let base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');

      // Add padding if needed
      while (base64.length % 4) {
        base64 += '=';
      }

      const payload = JSON.parse(atob(base64));
      return payload.exp * 1000; // Convert to milliseconds
    } catch (error) {
      // If JWT parsing fails, fall back to stored expires_at (from expires_in)
      const fallback = this.localStorage.getItem(this.ACCESS_EXPIRES_AT_KEY);
      return fallback ? Number(fallback) : null;
    }
  }

  // Check if token will expire within specified seconds
  isTokenExpiringSoon(secondsBeforeExpiry: number = 30): boolean {
    const token = this.getAccessToken();
    if (!token) {
      return true; // No token means we need to generate one
    }

    const expirationTime = this.getTokenExpirationTime();
    if (!expirationTime) {
      // Unknown expiry: don't force regeneration on every request.
      // Let the request proceed; 401 handling/interceptor can recover.
      return false;
    }

    const currentTime = Date.now();
    const timeUntilExpiry = expirationTime - currentTime;
    const secondsUntilExpiry = timeUntilExpiry / 1000;

    const isExpiringSoon = secondsUntilExpiry <= secondsBeforeExpiry;

    return isExpiringSoon;
  }

  // Check if token is expired
  isTokenExpired(): boolean {
    const token = this.getAccessToken();
    if (!token) return true;

    const expirationTime = this.getTokenExpirationTime();
    if (!expirationTime) {
      // Unknown expiry: treat as not expired to avoid infinite generate-token loops.
      return false;
    }

    return Date.now() >= expirationTime;
  }

  // Get time until token expires in seconds
  getTimeUntilExpiry(): number {
    const expirationTime = this.getTokenExpirationTime();
    if (!expirationTime) return 0;

    const currentTime = Date.now();
    const timeUntilExpiry = expirationTime - currentTime;
    return Math.max(0, timeUntilExpiry / 1000);
  }

  // Schedule automatic token refresh with specified buffer
  scheduleTokenRefresh(
    refreshCallback: () => void,
    secondsBeforeExpiry: number = 30,
  ): void {
    if (!isPlatformBrowser(this.platformId)) return;

    // Clear existing timer
    this.clearRefreshTimer();

    const expirationTime = this.getTokenExpirationTime();
    if (!expirationTime) return;

    const currentTime = Date.now();
    const timeUntilExpiry = expirationTime - currentTime;
    const refreshTime = timeUntilExpiry - secondsBeforeExpiry * 1000;

    if (refreshTime > 0) {
      this.refreshTimer = setTimeout(() => {
        refreshCallback();
        this.refreshTimer = null;
      }, refreshTime);
    } else {
      refreshCallback();
    }
  }

  // Clear the refresh timer
  clearRefreshTimer(): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  // Clear all authentication data
  clearAuth(): void {
    this.clearRefreshTimer();
    this.localStorage.removeitems('eol_access_token');
    this.localStorage.removeitems('eol_ref_token');
    this.localStorage.removeitems('eol_user_code');
    this.localStorage.removeitems('eolrefreshTokenInProgress');
    this.localStorage.removeitems(this.ACCESS_EXPIRES_AT_KEY);
  }

  // Store complete authentication data
  storeAuthData(authData: {
    access_token: string;
    refresh_token: string;
    user_code: string;
    expires_in?: number;
  }): void {
    this.setAccessToken(authData.access_token);
    this.setRefreshToken(authData.refresh_token);
    this.setUserCode(authData.user_code);
    // Persist expiry even when token isn't a JWT
    if (
      typeof authData.expires_in === 'number' &&
      isFinite(authData.expires_in) &&
      authData.expires_in > 0
    ) {
      const expiresAt = Date.now() + authData.expires_in * 1000;
      this.localStorage.setItem(this.ACCESS_EXPIRES_AT_KEY, String(expiresAt));
    }
  }
}
